import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useBooking } from "../context/BookingContext";
import { useContent } from "../context/ContentProvider";
import { supabase } from "../supabaseClient";
import DateRangePicker from "../components/DateRangePicker";
import RoomsStep from "./RoomsStep";
import ExtrasStep from "./ExtrasStep";
import CustomerStep from "./CustomerStep";
import Confirmation from "./Confirmation";

interface FormErrors {
  checkIn?: string;
  checkOut?: string;
  dateRange?: string;
}

const SearchStep: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { formData, updateFormData, hotelSlug, setCurrentStep, currentStep } = useBooking();
  const { content } = useContent();

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const urlParamsProcessed = useRef(false);

  // Handle URL parameters from widget - only once on mount
  useEffect(() => {
    if (urlParamsProcessed.current) return;

    const checkIn = searchParams.get("checkIn");
    const checkOut = searchParams.get("checkOut");
    const rooms = searchParams.get("rooms");
    const adults = searchParams.get("adults");
    const children = searchParams.get("children");
    const code = searchParams.get("code");

    if (checkIn || checkOut || rooms || adults || children || code) {
      console.log("🔗 Processing URL parameters:", {
        checkIn,
        checkOut,
        rooms,
        adults,
        children,
        code,
      });

      if (checkIn) updateFormData("checkIn", new Date(checkIn));
      if (checkOut) updateFormData("checkOut", new Date(checkOut));
      if (rooms) updateFormData("rooms", rooms);
      if (adults) updateFormData("adults", adults);
      if (children) updateFormData("children", children);
      if (code) updateFormData("promoCode", code);

      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("checkIn");
      newUrl.searchParams.delete("checkOut");
      newUrl.searchParams.delete("rooms");
      newUrl.searchParams.delete("adults");
      newUrl.searchParams.delete("children");
      newUrl.searchParams.delete("code");

      window.history.replaceState({}, "", newUrl.toString());
      urlParamsProcessed.current = true;
    }
  }, [searchParams, updateFormData]);

  // Fetch hotel data on component mount
  useEffect(() => {
    const fetchHotelData = async () => {
      console.log("🏨 Fetching hotel data for:", hotelSlug);

      try {
        const { data: hotel, error: hotelError } = await supabase
          .from("hotels")
          .select("id, name, address_city, currency")
          .ilike("name", `%${hotelSlug}%`)
          .limit(1)
          .maybeSingle();

        if (hotelError) {
          throw hotelError;
        }

        if (!hotel) {
          console.warn(
            `⚠️ Hotel not found: ${hotelSlug}, using fallback lookup`
          );

          const { data: fallbackHotel, error: fallbackError } = await supabase
            .from("hotels")
            .select("id, name, address_city, currency")
            .limit(1)
            .maybeSingle();

          if (fallbackError || !fallbackHotel) {
            throw new Error("No hotels available");
          }

          console.log("✅ Using fallback hotel:", fallbackHotel.name);
          return;
        }

        console.log("✅ Hotel data retrieved:", hotel.name);
        console.log("✅ Hotel data retrieved, rooms will be loaded by hook");
      } catch (error) {
        console.error("❌ Error fetching hotel data:", error);
      }
    };

    if (hotelSlug) {
      fetchHotelData();
    }
  }, [hotelSlug]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.checkIn) {
      newErrors.checkIn = "Date d'arrivée requise";
    }

    if (!formData.checkOut) {
      newErrors.checkOut = "Date de départ requise";
    }

    if (formData.checkIn && formData.checkOut) {
      if (formData.checkOut <= formData.checkIn) {
        newErrors.dateRange =
          "La date de départ doit être après la date d'arrivée";
      }

      const daysDiff = Math.ceil(
        (formData.checkOut.getTime() - formData.checkIn.getTime()) /
          (1000 * 60 * 60 * 24)
      );
      if (daysDiff > 7) {
        newErrors.dateRange =
          "Les réservations en ligne sont limitées à 7 nuits";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      console.log("✅ Proceeding to rooms step");
      setCurrentStep(2);
    } catch (error) {
      console.error("❌ Error during search:", error);
      alert(
        "Une erreur est survenue lors de la recherche. Veuillez réessayer."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Render different steps based on currentStep
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className=" bg-[#F5F3F0]">
            {/* Hero Section */}
            <div
              className="relative min-h-[75vh] flex items-center justify-center py-16 px-4"
              style={{
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/hero-image.png')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <div className="relative z-10 w-full max-w-[700px]">
                {/* Title */}
                <h1 className="text-[42px] md:text-[48px] leading-tight font-normal font-zodiak text-white mb-12 text-center drop-shadow-lg">
                  {content.heroTitle || ""}
                </h1>

                {/* Booking Card */}
                <div className="bg-white rounded-[12px] shadow-2xl p-8 md:p-10">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Date Range Picker */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sélectionner les dates
                      </label>
                      <DateRangePicker
                        checkIn={formData.checkIn}
                        checkOut={formData.checkOut}
                        onDateChange={(checkIn, checkOut) => {
                          updateFormData("checkIn", checkIn);
                          updateFormData("checkOut", checkOut);
                          setErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.checkIn;
                            delete newErrors.checkOut;
                            delete newErrors.dateRange;
                            return newErrors;
                          });
                        }}
                        error={errors.checkIn || errors.checkOut || errors.dateRange}
                        minDate={new Date()}
                      />
                    </div>

                    {/* Error Message */}
                    {(errors.checkIn || errors.checkOut || errors.dateRange) && (
                      <div className="text-sm text-red-600 -mt-2">
                        {errors.checkIn || errors.checkOut || errors.dateRange}
                      </div>
                    )}

                    {/* Guest Selection Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nombre de chambres
                        </label>
                        <select
                          value={formData.rooms}
                          onChange={(e) => updateFormData("rooms", e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-[12px] focus:ring-2 focus:ring-[#87764e] focus:border-[#87764e] transition-colors bg-white"
                        >
                          {[1, 2, 3, 4, 5].map((num) => (
                            <option key={num} value={num.toString()}>
                              {num} {num === 1 ? "Chambre" : "Chambres"}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Adultes
                        </label>
                        <select
                          value={formData.adults}
                          onChange={(e) => updateFormData("adults", e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-[12px] focus:ring-2 focus:ring-[#87764e] focus:border-[#87764e] transition-colors bg-white"
                        >
                          {[1, 2, 3, 4, 5, 6].map((num) => (
                            <option key={num} value={num.toString()}>
                              {num} {num === 1 ? "adulte" : "adultes"}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enfants
                        </label>
                        <select
                          value={formData.children}
                          onChange={(e) => updateFormData("children", e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-[12px] focus:ring-2 focus:ring-[#87764e] focus:border-[#87764e] transition-colors bg-white"
                        >
                          {[0, 1, 2, 3, 4].map((num) => (
                            <option key={num} value={num.toString()}>
                              {num} {num === 0 ? "enfant" : num === 1 ? "enfant" : "enfants"}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Promo Code */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Code promotionnel
                      </label>
                      <input
                        type="text"
                        placeholder="Code promo (optionnel)"
                        value={formData.promoCode}
                        onChange={(e) => updateFormData("promoCode", e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-[12px] focus:ring-2 focus:ring-[#87764e] focus:border-[#87764e] transition-colors"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-4 bg-[#87764e] hover:bg-[#7a6b47] text-white font-semibold text-base rounded-[12px] transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-wide"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Recherche en cours...
                        </span>
                      ) : (
                        "Rechercher"
                      )}
                    </button>

                    {/* Footer Note */}
                    <p className="text-xs text-gray-600 text-center leading-relaxed mt-4">
                      Les réservations en ligne sont limitées à 7 nuits.
                      <br />
                      Pour un séjour plus long, merci de nous écrire à{" "}
                      <a
                        href="mailto:reservation@hotel-adriatica.com"
                        className="text-[#67273f] hover:underline"
                      >
                        reservation@hotel-adriatica.com
                      </a>
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        );
      case 2:
        return <RoomsStep />;
      case 3:
        return <ExtrasStep />;
      case 4:
        return <CustomerStep />;
      case 5:
        return <Confirmation />;
      default:
        return <div>Step not found</div>;
    }
  };

  return renderCurrentStep();
};

export default SearchStep;